// =============================================
// ChangePassword Component
// שינוי סיסמה - משמש מהגדרות (voluntary) ומכניסה עם סיסמה פגה (forced)
// =============================================

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, X, KeyRound } from 'lucide-react';
import { authApi } from '../services/api';
import HelpTooltip from './common/HelpTooltip';
import { changePasswordHelpTexts } from '../constants/helpTexts';

interface ChangePasswordProps {
  mode: 'voluntary' | 'forced';
  onSuccess: () => void;
  onCancel?: () => void;
}

function validatePassword(password: string): string[] {
  const errors: string[] = [];
  if (password.length < 8) errors.push('לפחות 8 תווים');
  if (!/[A-Z]/.test(password)) errors.push('אות גדולה באנגלית');
  if (!/[a-z]/.test(password)) errors.push('אות קטנה באנגלית');
  if (!/[0-9]/.test(password)) errors.push('ספרה');
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) errors.push('תו מיוחד');
  return errors;
}

export default function ChangePassword({ mode, onSuccess, onCancel }: ChangePasswordProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const passwordErrors = newPassword ? validatePassword(newPassword) : [];
  const isNewPasswordValid = newPassword.length > 0 && passwordErrors.length === 0;
  const doPasswordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!currentPassword) {
      setError('יש להזין את הסיסמה הנוכחית');
      return;
    }

    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      setError(`הסיסמה חייבת לכלול: ${errors.join(', ')}`);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('הסיסמאות אינן תואמות');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authApi.changePassword(currentPassword, newPassword);

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        setError(result.message || 'שגיאה בשינוי הסיסמה');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'שגיאת רשת';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    paddingRight: '44px',
    paddingLeft: '44px',
    fontSize: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    outline: 'none',
    direction: 'ltr' as const,
    textAlign: 'right' as const,
    boxSizing: 'border-box' as const,
  };

  const iconStyle = {
    position: 'absolute' as const,
    right: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    pointerEvents: 'none' as const,
  };

  const eyeStyle = {
    position: 'absolute' as const,
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (success) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
        >
          <CheckCircle size={64} color="#22c55e" style={{ margin: '0 auto 16px' }} />
        </motion.div>
        <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
          הסיסמה שונתה בהצלחה
        </h3>
      </div>
    );
  }

  return (
    <div style={{ direction: 'rtl' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <KeyRound size={24} color="#f97316" />
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
            שינוי סיסמה
          </h3>
        </div>
        {mode === 'voluntary' && onCancel && (
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        )}
      </div>

      {mode === 'forced' && (
        <div style={{
          background: '#fef3c7',
          padding: '12px 16px',
          borderRadius: '12px',
          marginBottom: '20px',
          fontSize: '14px',
          color: '#92400e',
        }}>
          תוקף הסיסמה שלך פג. יש להגדיר סיסמה חדשה כדי להמשיך.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Current Password */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937' }}>סיסמה נוכחית</span>
          <HelpTooltip text={changePasswordHelpTexts.currentPassword.tooltip} />
        </div>
        <div style={{ position: 'relative', marginBottom: '16px' }}>
          <Lock size={18} style={iconStyle} />
          <input
            type={showCurrent ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="סיסמה נוכחית"
            style={inputStyle}
            required
          />
          <button type="button" style={eyeStyle} onClick={() => setShowCurrent(!showCurrent)}>
            {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {/* New Password */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937' }}>סיסמה חדשה</span>
          <HelpTooltip text={changePasswordHelpTexts.newPassword.tooltip} />
        </div>
        <div style={{ position: 'relative', marginBottom: '8px' }}>
          <Lock size={18} style={iconStyle} />
          <input
            type={showNew ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="סיסמה חדשה"
            style={inputStyle}
            required
          />
          <button type="button" style={eyeStyle} onClick={() => setShowNew(!showNew)}>
            {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {/* Password requirements */}
        {newPassword && (
          <div style={{ marginBottom: '12px' }}>
            {[
              { label: 'לפחות 8 תווים', valid: newPassword.length >= 8 },
              { label: 'אות גדולה באנגלית', valid: /[A-Z]/.test(newPassword) },
              { label: 'אות קטנה באנגלית', valid: /[a-z]/.test(newPassword) },
              { label: 'ספרה', valid: /[0-9]/.test(newPassword) },
              { label: 'תו מיוחד', valid: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) },
            ].map((req, i) => (
              <div key={i} style={{
                fontSize: '12px',
                marginTop: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: req.valid ? '#22c55e' : '#ef4444',
              }}>
                {req.valid ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                {req.label}
              </div>
            ))}
          </div>
        )}

        {/* Confirm Password */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937' }}>אישור סיסמה חדשה</span>
          <HelpTooltip text={changePasswordHelpTexts.confirmPassword.tooltip} />
        </div>
        <div style={{ position: 'relative', marginBottom: '8px' }}>
          <Lock size={18} style={iconStyle} />
          <input
            type={showConfirm ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="אישור סיסמה חדשה"
            style={inputStyle}
            required
          />
          <button type="button" style={eyeStyle} onClick={() => setShowConfirm(!showConfirm)}>
            {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {/* Match indicator */}
        {confirmPassword && (
          <div style={{
            fontSize: '12px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: doPasswordsMatch ? '#22c55e' : '#ef4444',
          }}>
            {doPasswordsMatch ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
            {doPasswordsMatch ? 'הסיסמאות תואמות' : 'הסיסמאות אינן תואמות'}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background: '#fee2e2',
            color: '#dc2626',
            padding: '12px',
            borderRadius: '10px',
            marginBottom: '16px',
            fontSize: '14px',
            textAlign: 'center',
          }}>
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading || !currentPassword || !isNewPasswordValid || !doPasswordsMatch}
          style={{
            width: '100%',
            padding: '14px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: 'white',
            background: isLoading || !currentPassword || !isNewPasswordValid || !doPasswordsMatch
              ? '#d1d5db'
              : 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
            border: 'none',
            borderRadius: '12px',
            cursor: isLoading || !currentPassword || !isNewPasswordValid || !doPasswordsMatch
              ? 'not-allowed'
              : 'pointer',
          }}
        >
          {isLoading ? 'שומר...' : 'שמור סיסמה חדשה'}
        </button>
      </form>
    </div>
  );
}
